// ------------------------------------------------------------------------
// Promise implementation from the Senna framework
//
//  Acts as a wrapper around the native promise implementation
//  with easy context binding.
//
// Author: Chris Pearce <hello@chrispearce.me>
// Github: TBC
// License: MIT
//
// Dependencies: Constructor
'use strict';

var _ = require('underscore');
var Constructor = require('js/vendor/constructor');


var root = this;

var STATUS = {
  WAITING: 1,
  FULFILLED: 2,
  BROKEN: 3
};

var Promise = Constructor.extend({
  // Callbacks
  success: null,
  error: null,

  // Current promise status
  status: STATUS.WAITING,

  // Array of chained promises
  chain: null,

  // Payload of the promise
  payload: null
});

Promise.prototype.initialize = function(success, error, context) {

  // Allow context as the last parameter with variable optional arguments
  if (error && typeof error !== 'function') {
    context = error;
    error = null;
  }

  // Setup callbacks
  success && (this.success = _.bind(success, context));
  error && (this.error = _.bind(error, context));

  // Make a fresh promise chain
  this.chain = [];
};

// Destroy this promise, stopping any chained operations. WILL NOT CALL ERROR HANDLERS
Promise.prototype.destroy = function() {
  this.status = STATUS.BROKEN;
  this.chain.length = 0;
};

// Call any chained promises
var progressChain = function(promise, payload) {
  var chainLength = promise.chain.length;

  for (var i = 0; i < chainLength; ++i) {

    // If there is an error progress the chain only if we are not handling errors at this point
    if (promise.status === STATUS.FULFILLED) {
      promise.chain[i].fulfil.call(promise.chain[i], payload);
    } else if (!promise.error) {
      promise.chain[i].break.call(promise.chain[i], payload);
    }

  }

  // Clear the chain. we are done!
  promise.chain.length = 0;
};

// translate promise - take the status and perform relevant behaviour
var translate = function(promise, status, payload) {

  // Set the status (can only be completed ONCE!)
  if (promise.status !== STATUS.WAITING) {
    throw new Error('Cannot complete an already completed promise!');
  }
  promise.status = status;

  // Setup our payload (this can only happen ONCE!)
  if (payload && promise.payload) {
    throw new Error('Cannot override payload!');
  }
  promise.payload = payload;

  var method = null;

  switch (promise.status) {
    case STATUS.FULFILLED:
      method = promise.success;
      break;
    case STATUS.BROKEN:
      method = promise.error;
      break;
  }

  // If we have a callback to handle
  if (typeof method == 'function') {
    var newPromise = method.call(root, promise.payload);

    // If our callback returns a promise we throw the rest of our chain into that new one
    //  Therefore waiting for that promise to be fulfilled before proceeding
    if (newPromise instanceof Promise) {
      newPromise.chain = newPromise.chain.concat(promise.chain);
      promise.chain.length = 0;

      // If the promise (from callback) is completed we need to progress it straight away. Otherwise wait.
      if (newPromise.status !== STATUS.WAITING) {
        progressChain(newPromise, newPromise.payload);
      }

      return;

      // Allow overriding of payload through chain
    } else if (newPromise) {
      progressChain(promise, newPromise);

      // Added this as promises added to the chain of an already complete promise later never see this new payload
      // - If we see any *new* dodgy promise behaviour down the line this could potentially be the cause!
      promise.payload = newPromise;

      return;
    }
  }

  progressChain(promise, payload);
};

// Fulfils this promise - invokes any currently waiting functions
Promise.prototype.fulfil = function(payload) {
  translate(this, STATUS.FULFILLED, payload);
};

// Break this promise - invokes any currently waiting functions
Promise.prototype.break = function(payload) {
  translate(this, STATUS.BROKEN, payload);
};

// Request that a new promise be created for when this promise's payload arrives
Promise.prototype.then = function(success, error, context) {

  var newPromise = (success instanceof Promise) ? success : (new Promise(success, error, context));

  // If this promise is already completed we can just translate straight away
  //  otherwise we will need to push it on to the chain and wait
  if (this.status !== STATUS.WAITING) {
    translate(newPromise, this.status, this.payload);
  } else {
    this.chain.push(newPromise);
  }

  return newPromise;
};

// Catch success or failure
Promise.prototype.finally = function(callback, context) {
  return this.then(callback, callback, context);
};

// Check for specific promise states
Promise.prototype.isFulfilled = function() {
  return this.status === STATUS.FULFILLED;
};
Promise.prototype.isBroken = function() {
  return this.status === STATUS.BROKEN;
};
Promise.prototype.isPending = function() {
  return this.status === STATUS.WAITING;
};

// Pre-fulfilled promise
Promise.fulfilled = function(payload) {
  var promise = new Promise();

  promise.fulfil(payload);

  return promise;
};

// Pre-broken promise
Promise.broken = function(payload) {
  var promise = new Promise();

  promise.break(payload);

  return promise;
};

/* Utility method for batching some promises
 * Returns: fulfilledResults when all requests succeed,
 * 			{fulfilledResults, brokenResults} object if at least one request fails.
 */
Promise.batch = function(promises) {
  var fulfilledResults = {};
  var brokenResults = {};
  var total = Object.keys(promises).length;
  var noofFinished = -1;
  var newPromise = new Promise();
  var failed = false;

  var increment = function(promiseBroken) {
    if (promiseBroken) {
      failed = true;
    }

    if (++noofFinished == total) {

      if (failed) {
        var promiseResult = {
          "fulfilledResults": fulfilledResults,
          "brokenResults": brokenResults
        };

        newPromise.break(promiseResult);

      } else {
        newPromise.fulfil(fulfilledResults);
      }
    }
  };

  // Process requests
  for (var key in promises) {
    if (promises.hasOwnProperty(key)) {
      promises[key].then(function(payload) {
        fulfilledResults[key] = payload;
        increment(false);
      }, function(payload) {
        brokenResults[key] = payload;
        increment(true);
      });
    }
  }

  increment();

  return newPromise;
};

/**
 * Run a series of promises in serial
 *
 * callbacks look something like:
 * [ () => return new promise(),
 *   () => return new promise(),
 *   () => return true,           Even if a callback doesn't return a promise we can continue the chain
 *   () => return new promise() ]
 *
 * Resulting flow looks something like:
 * callbacks[0].finally(callbacks[1]).finally(callbacks[2]).finally(callbacks[3]);
 *
 * @param {function[]} callbacks Array of functions which can return promises
 * @param {Boolean} [force] Continue chain if one promise fails
 * @returns {Promise}
 */
Promise.serial = function(callbacks, force) {
  var current, newPromise;
  current = newPromise = new Promise();

  for (var i = 0, l = callbacks.length; i < l; ++i) {
    current = current[force ? 'finally' : 'then'](callbacks[i]);
  }

  // Kick off our chain which will wait for further promises before actually completing
  newPromise.fulfil();

  return newPromise;
};

module.exports = Promise;



// WEBPACK FOOTER //
// ./app/assets/js/vendor/promise.js